import {
  type MapDefinition,
  type Region,
  type Territory,
  regionId,
  territoryId,
} from './schema.js';

const NA = regionId('na');
const SA = regionId('sa');
const EURO = regionId('euro');
const AFRICA = regionId('africa');
const ASIA = regionId('asia');

// Layout: traditional top-down world map projection. Five continents
// in their geographic positions; cross-continent bridges represent
// real-world straits and land bridges (Bering, Atlantic, Panama,
// Mediterranean, Sinai, Eurasian land border).
//
//   ┌──┬──┐         ┌──┐                  ┌──┬──┬──┐
//   │NW│NE│         │N │                  │NW│N │NE│
//   ├──┼──┤      ┌──┼──┼──┐                ├──┼──┼──┤
//   │SW│SE│      │W │M │E │                │W │M │E │
//   └──┴──┘      └──┼──┼──┘                ├──┼──┼──┘
//       NA           │S │                   │SW│S │
//                    └──┘                   └──┴──┘
//                  Euro (5)                  Asia (8)
//
//   ┌──┐              ┌──┐
//   │N │              │N │
//   ├──┼──┐         ┌──┼──┼──┐
//   │W │E │         │W │M │E │     ← actually Africa is the simpler
//   ├──┘             ├──┘            T-shape, same as SA
//   │S │              │S │
//   └──┘              └──┘
//      SA                Africa
//
// Tile sizing: 72px (smaller than Ring's 80, larger than the v0.6
// initial 64). On a typical phone (iPhone 14 Pro at 393 CSS px wide),
// 72px design tiles render at ~39pt physical — close to iOS HIG 44pt
// but still in stress-test territory.
//
// Inter-continent gap: 56px ("ocean") so each continent reads as a
// separate landmass on a world map, not a single packed grid. The
// trade-off is longer adjacency lines for the cross-continent
// bridges, which is geographically right — Bering, Atlantic, Sinai
// etc. ARE long crossings.
//
// The 72-px tiles fill more of the canvas's wasted space (especially
// below the bottom row) than the prior 64-px version. Margins are
// roughly equal on all four sides (~16-47 px).
//
// Cross-continent bridges (6 total):
//   na_ne  ↔ euro_n      North Atlantic / Iceland
//   na_se  ↔ sa_n        Panama
//   sa_e   ↔ africa_w    Mid-Atlantic crossing
//   africa_n ↔ euro_s    Mediterranean
//   africa_n ↔ asia_sw   Sinai
//   euro_e ↔ asia_w      Eurasian land border
//
// (Bering Strait — na_nw ↔ asia_ne — intentionally OMITTED. Standard
// Risk has it; BattleShark drops it to encourage quicker games. Asia
// has enough attack vectors from the west via euro_e and africa_n;
// once it's captured the holder shouldn't also get a free wraparound
// shortcut into NA. Without Bering, Asia and NA are 4+ hops apart on
// the connectivity graph — buys the defender breathing room.)
//
// africa_n carries TWO bridges (Mediterranean + Sinai) — the central
// crossroads. Losing it cuts Africa off from both Europe AND Asia.
// The v0.5 AI's region-scoring will weight it heavily.
//
// SE corner of Asia is missing (no asia_se) — keeps Asia at exactly 8
// tiles per the +5 bonus design. Asia M is the central hub; losing
// it splits Asia into NW and SW halves.

const T = {
  // North America (2×2 block)
  naNW: territoryId('na_nw'),
  naNE: territoryId('na_ne'),
  naSW: territoryId('na_sw'),
  naSE: territoryId('na_se'),
  // Europe / "Euro" (plus-shape, 5 tiles)
  euroN: territoryId('euro_n'),
  euroW: territoryId('euro_w'),
  euroM: territoryId('euro_mid'),
  euroE: territoryId('euro_e'),
  euroS: territoryId('euro_s'),
  // Asia (3×3 minus SE corner, 8 tiles)
  asiaNW: territoryId('asia_nw'),
  asiaN: territoryId('asia_n'),
  asiaNE: territoryId('asia_ne'),
  asiaW: territoryId('asia_w'),
  asiaM: territoryId('asia_mid'),
  asiaE: territoryId('asia_e'),
  asiaSW: territoryId('asia_sw'),
  asiaS: territoryId('asia_s'),
  // South America (T-shape, 4 tiles)
  saN: territoryId('sa_n'),
  saW: territoryId('sa_w'),
  saE: territoryId('sa_e'),
  saS: territoryId('sa_s'),
  // Africa (T-shape, 4 tiles — same shape as SA)
  africaN: territoryId('africa_n'),
  africaW: territoryId('africa_w'),
  africaE: territoryId('africa_e'),
  africaS: territoryId('africa_s'),
} as const;

// Bonuses chosen to reflect tile count: +3 for the four-tile regions
// (NA / SA / Africa), +4 for Euro (5 tiles), +5 for Asia (8 tiles).
// Total pool = 18 — much higher than Ring's 9. Multiple regions are
// hold-able simultaneously, which is the point of a larger map.
const regions: readonly Region[] = [
  { id: NA, name: 'North America', territoryBonus: 3 },
  { id: SA, name: 'South America', territoryBonus: 3 },
  { id: AFRICA, name: 'Africa', territoryBonus: 3 },
  { id: EURO, name: 'Euro', territoryBonus: 4 },
  { id: ASIA, name: 'Asia', territoryBonus: 5 },
];

const territories: readonly Territory[] = [
  // --- North America (4 tiles, top-left 2×2) ---
  {
    id: T.naNW,
    name: 'NA NW',
    region: NA,
    position: { x: 52, y: 333 },
    adjacent: [T.naNE, T.naSW],
  },
  {
    id: T.naNE,
    name: 'NA NE',
    region: NA,
    position: { x: 124, y: 333 },
    adjacent: [T.naNW, T.naSE, T.euroN],
  },
  {
    id: T.naSW,
    name: 'NA SW',
    region: NA,
    position: { x: 52, y: 405 },
    adjacent: [T.naNW, T.naSE],
  },
  {
    id: T.naSE,
    name: 'NA SE',
    region: NA,
    position: { x: 124, y: 405 },
    adjacent: [T.naNE, T.naSW, T.saN],
  },

  // --- Euro (5 tiles, plus-shape, top-mid) ---
  // Euro M is the central hub — losing it isolates all four arms.
  {
    id: T.euroN,
    name: 'Euro N',
    region: EURO,
    position: { x: 324, y: 333 },
    adjacent: [T.euroM, T.naNE],
  },
  {
    id: T.euroW,
    name: 'Euro W',
    region: EURO,
    position: { x: 252, y: 405 },
    adjacent: [T.euroM],
  },
  {
    id: T.euroM,
    name: 'Euro Mid',
    region: EURO,
    position: { x: 324, y: 405 },
    adjacent: [T.euroN, T.euroW, T.euroE, T.euroS],
  },
  {
    id: T.euroE,
    name: 'Euro E',
    region: EURO,
    position: { x: 396, y: 405 },
    adjacent: [T.euroM, T.asiaW],
  },
  {
    id: T.euroS,
    name: 'Euro S',
    region: EURO,
    position: { x: 324, y: 477 },
    adjacent: [T.euroM, T.africaN],
  },

  // --- Asia (8 tiles, 3×3 minus SE corner, top-right) ---
  // Asia M is the hub; the missing SE corner means asia_e and asia_s
  // are both dead ends without M.
  {
    id: T.asiaNW,
    name: 'Asia NW',
    region: ASIA,
    position: { x: 524, y: 333 },
    adjacent: [T.asiaN, T.asiaW],
  },
  {
    id: T.asiaN,
    name: 'Asia N',
    region: ASIA,
    position: { x: 596, y: 333 },
    adjacent: [T.asiaNW, T.asiaNE, T.asiaM],
  },
  {
    id: T.asiaNE,
    name: 'Asia NE',
    region: ASIA,
    position: { x: 668, y: 333 },
    adjacent: [T.asiaN, T.asiaE],
  },
  {
    id: T.asiaW,
    name: 'Asia W',
    region: ASIA,
    position: { x: 524, y: 405 },
    adjacent: [T.asiaNW, T.asiaM, T.asiaSW, T.euroE],
  },
  {
    id: T.asiaM,
    name: 'Asia Mid',
    region: ASIA,
    position: { x: 596, y: 405 },
    adjacent: [T.asiaN, T.asiaW, T.asiaE, T.asiaS],
  },
  {
    id: T.asiaE,
    name: 'Asia E',
    region: ASIA,
    position: { x: 668, y: 405 },
    adjacent: [T.asiaNE, T.asiaM],
  },
  {
    id: T.asiaSW,
    name: 'Asia SW',
    region: ASIA,
    position: { x: 524, y: 477 },
    adjacent: [T.asiaW, T.asiaS, T.africaN],
  },
  {
    id: T.asiaS,
    name: 'Asia S',
    region: ASIA,
    position: { x: 596, y: 477 },
    adjacent: [T.asiaSW, T.asiaM],
  },

  // --- South America (4 tiles, T-shape, bottom-left, under NA) ---
  // W is the internal hub; N/E/S each connect only to W.
  {
    id: T.saN,
    name: 'SA N',
    region: SA,
    position: { x: 52, y: 605 },
    adjacent: [T.saW, T.naSE],
  },
  {
    id: T.saW,
    name: 'SA W',
    region: SA,
    position: { x: 52, y: 677 },
    adjacent: [T.saN, T.saE, T.saS],
  },
  {
    id: T.saE,
    name: 'SA E',
    region: SA,
    position: { x: 124, y: 677 },
    adjacent: [T.saW, T.africaW],
  },
  {
    id: T.saS,
    name: 'SA S',
    region: SA,
    position: { x: 52, y: 749 },
    adjacent: [T.saW],
  },

  // --- Africa (4 tiles, T-shape, bottom-mid, under Euro) ---
  // africa_n is the strategic chokepoint — bears BOTH the Mediterranean
  // and Sinai bridges. Losing it cuts Africa off from Eurasia entirely.
  {
    id: T.africaN,
    name: 'Africa N',
    region: AFRICA,
    position: { x: 252, y: 605 },
    adjacent: [T.africaW, T.euroS, T.asiaSW],
  },
  {
    id: T.africaW,
    name: 'Africa W',
    region: AFRICA,
    position: { x: 252, y: 677 },
    adjacent: [T.africaN, T.africaE, T.africaS, T.saE],
  },
  {
    id: T.africaE,
    name: 'Africa E',
    region: AFRICA,
    position: { x: 324, y: 677 },
    adjacent: [T.africaW],
  },
  {
    id: T.africaS,
    name: 'Africa S',
    region: AFRICA,
    position: { x: 252, y: 749 },
    adjacent: [T.africaW],
  },
];

export const terraMap: MapDefinition = {
  id: 'terra',
  name: 'Terra',
  regions,
  territories,
  // 72px tiles (vs Ring/Slash's default 80). The smaller footprint is
  // what lets five well-separated continents fit into the 720×600 map
  // zone with visible ocean (~56px) between landmasses, while still
  // using more of the canvas than the v0.6 initial 64-px version.
  tileSize: 72,
};
